import React, { useEffect } from 'react';
import {
  Badge, Button, Card, Container, Form, Stack,
} from 'react-bootstrap';
import { ArrowLeft, Link45deg, Save } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  featureFlagDefinitions,
  featureFlagKeys,
  isFlagEffective,
  resetFeatureFlags,
  selectFeatureFlagSources,
  selectFeatureFlags,
  setFeatureFlag,
  FeatureFlagKey,
  FeatureFlags,
} from '../features/featureFlags/featureFlagsSlice';
import { sequoiaURL } from '../lib/config';
import PineappleNav from './Nav';
import PineappleAlert from './PineappleAlert';
import './FeatureFlagsPage.css';

/**
 * /flags: the feature flags, one row each with a switch, saved in this
 * browser's localStorage. An internal page for Pineapple staff, not linked
 * from the form. What a row has to answer in a glance: is it on, where did
 * that come from, and will it stay after a reload.
 */

/** Takes a flag's parameter out of the address, so a reload does not quietly
 * put the URL's value back over the choice just saved. */
const dropFromUrl = (key: FeatureFlagKey) => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(key)) return;
  url.searchParams.delete(key);
  window.history.replaceState(window.history.state, '', url.toString());
};

/** The form, with whatever flags the address carries still in it. */
const formHref = () => `/${window.location.search}`;

const since = (day: string) => {
  const d = dayjs(day);
  return d.isValid() ? d.format('D MMM YYYY') : day;
};

interface FlagRowProps {
  flagKey: FeatureFlagKey;
  flags: FeatureFlags;
  source: 'default' | 'stored' | 'url';
  onToggle: (key: FeatureFlagKey, value: boolean) => void;
}

const FlagRow = ({
  flagKey, flags, source, onToggle,
}: FlagRowProps) => {
  const def = featureFlagDefinitions[flagKey];
  const on = flags[flagKey];
  const parent = def.requires ? featureFlagDefinitions[def.requires] : undefined;
  const effective = isFlagEffective(flagKey, flags);
  const placeholder = def.status === 'placeholder';
  const inert = !effective || placeholder;
  const descriptionId = `flag_${flagKey}_description`;
  const statusId = `flag_${flagKey}_status`;

  let status: string;
  if (source === 'url') {
    status = `${on ? 'On' : 'Off'}, from the URL you opened. This tab only, not saved.`;
  } else if (source === 'stored') {
    status = `${on ? 'On' : 'Off'}, saved on this browser. Stays after a reload.`;
  } else {
    status = `${on ? 'On' : 'Off'}, the normal setting.`;
  }

  return (
    <Card
      data-flag={flagKey}
      className={`flags-row shadow-sm border-0 rounded-4 p-3${inert ? ' flags-row-inert' : ''}${parent ? ' flags-row-dependent' : ''}`}
    >
      <div className="d-flex align-items-start gap-3">
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <label htmlFor={`flag_${flagKey}`} className="fw-bold fs-5 mb-0">{def.label}</label>
            {source === 'stored' && (
              <Badge bg="primary" className="rounded-pill fw-normal">
                <Save className="me-1 mb-1" aria-hidden />
                saved on this browser
              </Badge>
            )}
            {source === 'url' && (
              <Badge bg="warning" text="dark" className="rounded-pill fw-normal">
                <Link45deg className="me-1 mb-1" aria-hidden />
                from the URL, this tab only
              </Badge>
            )}
            {placeholder && (
              <Badge bg="light" text="dark" className="rounded-pill fw-normal">
                not wired up yet (placeholder)
              </Badge>
            )}
          </div>
          <p id={descriptionId} className="mb-1 mt-2">{def.description}</p>
          {parent && !effective && (
            <p className="mb-1 fw-semibold">{`Needs ${parent.label} on: this switch does nothing until it is.`}</p>
          )}
          {placeholder && (
            <p className="mb-1 fw-semibold">Not connected yet: switching this does nothing in the form.</p>
          )}
          <p id={statusId} className="mb-1 text-body-secondary">{status}</p>
          <p className="mb-0 small text-body-secondary font-monospace">
            {`${flagKey} · normally ${def.defaultValue ? 'on' : 'off'} · added ${since(def.since)}`}
          </p>
        </div>
        <div className="text-center flex-shrink-0 pt-1">
          <Form.Check
            type="switch"
            className="flags-switch"
            id={`flag_${flagKey}`}
            role="switch"
            aria-checked={on}
            aria-describedby={`${descriptionId} ${statusId}`}
            checked={on}
            disabled={!effective}
            onChange={(e) => onToggle(flagKey, e.target.checked)}
          />
          <div className="small fw-semibold" aria-hidden>{on ? 'On' : 'Off'}</div>
        </div>
      </div>
    </Card>
  );
};

const FeatureFlagsPage = () => {
  const dispatch = useAppDispatch();
  const flags = useAppSelector(selectFeatureFlags);
  const sources = useAppSelector(selectFeatureFlagSources);
  const saved = featureFlagKeys.filter((key) => sources[key] === 'stored').length;
  const fromUrl = featureFlagKeys.filter((key) => sources[key] === 'url').length;

  useEffect(() => {
    document.title = 'Feature flags | Pineapple Net';
  }, []);

  const onToggle = (key: FeatureFlagKey, value: boolean) => {
    dispatch(setFeatureFlag({ key, value }));
    dropFromUrl(key);
  };

  // Parents first, each followed by the flags that depend on it.
  const ordered = featureFlagKeys
    .filter((key) => !featureFlagDefinitions[key].requires)
    .flatMap((key) => [key, ...featureFlagKeys.filter((k) => featureFlagDefinitions[k].requires === key)]);

  let summary: string;
  if (saved === 0) {
    summary = 'Nothing changed on this browser.';
  } else {
    summary = `${saved} ${saved === 1 ? 'flag' : 'flags'} changed on this browser.`;
  }

  return (
    <>
      <PineappleNav />
      <Container className="px-2 px-md-0 py-3" style={{ maxWidth: 720 }}>
        <h1 className="text-primary fw-bold">Feature flags</h1>
        <p className="mb-2">
          Internal page for Pineapple staff. Switches save in this browser and stay on after
          a reload. A flag in the address, for example
          {' '}
          <span className="font-monospace">?communityFibre=true</span>
          , wins for this tab only and is never saved.
        </p>
        <p className="fw-semibold mb-3" aria-live="polite">{summary}</p>
        {fromUrl > 0 && (
          <PineappleAlert className="text-black">
            Flags set in the address win while this tab is open. Switching one here saves
            your choice on this browser instead.
          </PineappleAlert>
        )}

        <Stack gap={3} className="my-3">
          {ordered.map((key) => (
            <FlagRow key={key} flagKey={key} flags={flags} source={sources[key]} onToggle={onToggle} />
          ))}
        </Stack>

        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mt-4">
          <Button variant="link" className="text-primary fw-bold ps-0" href={formHref()}>
            <ArrowLeft className="me-1 mb-1" aria-hidden />
            Back to the signup form
          </Button>
          <Button
            id="flags_reset"
            variant="outline-dark"
            disabled={saved === 0}
            onClick={() => dispatch(resetFeatureFlags())}
          >
            {saved === 0 ? 'Reset to defaults' : `Reset ${saved} ${saved === 1 ? 'change' : 'changes'} to defaults`}
          </Button>
        </div>

        <p className="small text-body-secondary mt-4 mb-0 text-break">
          {`Environment: ${process.env.REACT_APP_ENV || 'development'}. Sequoia: `}
          <span className="font-monospace">{sequoiaURL}</span>
        </p>
      </Container>
    </>
  );
};

export default FeatureFlagsPage;
